import { notEmpty, theme, useResponsive } from "@product/scmp-sdk";
import first from "lodash/first";
import last from "lodash/last";
import type { FunctionComponent } from "react";
import { Fragment, useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/article/article-render";
import { useArticleBaseAdvertisingInfo } from "~/components/article/article-render/hooks";
import { GenericPostiesArticleDigitalArchiveLink } from "~/components/article/article-render/posties/generic-posties-article/generic-posties-article-digital-archive-link";
import {
  GenericPostiesArticleTableThead,
  GenericPostiesArticleTableTr,
} from "~/components/article/article-render/posties/generic-posties-article/table/styles";
import { ArticleSpeechSkipContainer } from "~/components/article/article-speech";
import { ContentBody } from "~/components/content/content-body";
import { PostiesPaywallContainer } from "~/components/posties/paywall-container";
import type { ExtraProps as LinkExtraProps } from "~/components/schema-render/common/link";
import { useContentSchemaRenderLink } from "~/components/schema-render/common/link/hooks";
import { formatInTimeZone } from "~/lib/utils";
import type { genericPostiesArticleContent$key } from "~/queries/__generated__/genericPostiesArticleContent.graphql";

import { GenericPostiesArticleBlockquoteQuote } from "./blockquote-quote";
import {
  Authors,
  AuthorsAndPublishedDate,
  Content,
  DesktopLeft,
  DesktopRight,
  GenericPostiesArticleContainer,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  Headline,
  ListenNow,
  MobileBottom,
  MobileTop,
  PublishedDate,
  Right,
  Section,
  StyledGenericPostiesArticleVocabBox,
  StyledGenericPostiesLeading,
  StyledPostiesSubHeadline,
  StyledTopBackgroundImage,
  TopBackgroundContainer,
  TopBlock,
} from "./styles";
import { GenericPostiesArticleTable } from "./table";
import TopBackground from "./top-background.png";

type Props = {
  provider: DefaultComponentsProvider;
  reference: genericPostiesArticleContent$key;
};

export const GenericPostiesArticle: FunctionComponent<Props> = ({
  provider,
  reference: reference_,
}) => {
  const article = useFragment(
    graphql`
      fragment genericPostiesArticleContent on Article
      @argumentDefinitions(customContents: { type: "[CustomContentInput]", defaultValue: [] }) {
        sections {
          value {
            name
            urlAlias
          }
        }
        authors {
          name
        }
        publishedDate
        additionalInfoBoxes {
          ...articleAdditionalInfoBoxArticle
        }
        ...genericPostiesArticleVocabBoxArticle
        ...blockquoteQuoteGenericPostiesArticleContent
        ...articleBaseAdvertisingInfoArticle
        ...contentBodyContent @arguments(customContents: $customContents)
      }
    `,
    reference_,
  );

  const additionalInfoBoxes = article?.additionalInfoBoxes
    ?.filter(notEmpty)
    .map?.((item, index) => (
      <Fragment key={index}>{provider.articleAdditionalInfoBox({ reference: item })}</Fragment>
    ));

  const [mobileTopAdditionInfoBox, ...mobileBottomAdditionInfoBoxes] = additionalInfoBoxes ?? [];

  const advertising = useArticleBaseAdvertisingInfo(article);
  const section = last(first(article?.sections)?.value);

  const linkExtraProps = useMemo<LinkExtraProps>(
    () => ({
      digitalArchiveLinkComponentRenderFunction: props => (
        <GenericPostiesArticleDigitalArchiveLink {...props} />
      ),
    }),
    [],
  );
  const { ComponentWithExtraProps: LinkWithExtraProps } =
    useContentSchemaRenderLink(linkExtraProps);

  const isDesktopAndAbove = useResponsive(theme.breakpoints.up("desktop"), true);

  return (
    <GenericPostiesArticleContainer>
      <TopBackgroundContainer>
        <StyledTopBackgroundImage alt={TopBackground.alt} src={TopBackground.src} />
      </TopBackgroundContainer>
      <TopBlock>
        {notEmpty(section) && (
          <ArticleSpeechSkipContainer>
            <Section pathname={section.urlAlias}>{section?.name}</Section>
          </ArticleSpeechSkipContainer>
        )}
        <Headline>{provider.headline()}</Headline>
        <AuthorsAndPublishedDate>
          <Authors>{article?.authors?.map(author => author?.name).join(", ")}</Authors>
          <PublishedDate>
            {article?.publishedDate &&
              `${formatInTimeZone(article.publishedDate ?? "", "d MMM, yyyy")}`}
          </PublishedDate>
        </AuthorsAndPublishedDate>
      </TopBlock>
      <StyledPostiesSubHeadline>{provider.subHeadline()}</StyledPostiesSubHeadline>
      <StyledGenericPostiesLeading provider={provider} showTitle={isDesktopAndAbove} />
      <DesktopLeft>{additionalInfoBoxes}</DesktopLeft>
      <MobileTop>
        {mobileTopAdditionInfoBox}
        <ArticleSpeechSkipContainer>
          <ListenNow>
            {provider.speechPlayer({
              variant: "posties",
            })}
          </ListenNow>
        </ArticleSpeechSkipContainer>
      </MobileTop>
      <Content>
        <ContentBody
          advertising={advertising}
          extraComponentMap={{
            a: LinkWithExtraProps,
            h1: Heading1,
            h2: Heading2,
            h3: Heading3,
            h4: Heading4,
            h5: Heading5,
            h6: Heading6,
            "inline-widget": null,
            table: GenericPostiesArticleTable,
            thead: GenericPostiesArticleTableThead,
            tr: GenericPostiesArticleTableTr,
          }}
          extraRenderFunctionMap={{
            "blockquote-quote": (
              props,
              itemIndex,
              index,
              _children,
              schemaNode,
              parentSchemaNode,
            ) => (
              <GenericPostiesArticleBlockquoteQuote
                {...props}
                index={index}
                key={itemIndex}
                parentSchemaNode={parentSchemaNode}
                reference={article}
                schemaNode={schemaNode}
              />
            ),
          }}
          reference={article}
        />
      </Content>
      <DesktopRight>
        <ArticleSpeechSkipContainer>
          <ListenNow>
            {provider.speechPlayer({
              variant: "posties",
            })}
          </ListenNow>
        </ArticleSpeechSkipContainer>
      </DesktopRight>
      <MobileBottom>{mobileBottomAdditionInfoBoxes}</MobileBottom>
      <PostiesPaywallContainer />
      <Right />
      <StyledGenericPostiesArticleVocabBox reference={article} />
    </GenericPostiesArticleContainer>
  );
};

GenericPostiesArticle.displayName = "GenericPostiesArticle";
